import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { CheckCircle, Users, Target, Award, Globe, ArrowRight, Sparkles, Rocket, Shield, HeartHandshake } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutPage = () => {
  const pageRef = useRef(null);
  const [stats, setStats] = useState({ customers: '30+', projects_done: '38', results_guaranteed: '100' });
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    // Fetch stats
    fetch(`${API_URL}/api/stats`)
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => {});
    
    // Fetch company info
    fetch(`${API_URL}/api/company-info`)
      .then(res => res.json())
      .then(data => setCompanyInfo(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const values = [
    { icon: Target, title: 'Mission-Driven', description: 'Delivering exceptional IT solutions that drive business growth and operational excellence.' },
    { icon: Shield, title: 'Quality First', description: 'We prioritize quality in every project, ensuring reliable and scalable solutions.' },
    { icon: HeartHandshake, title: 'Client Focus', description: 'Building lasting partnerships through transparent communication and dedicated support.' },
    { icon: Rocket, title: 'Innovation', description: 'Embracing cutting-edge technologies to keep your business ahead of the curve.' },
  ];

  const locations = [
    { name: 'Europe', flag: '🇪🇺' },
    { name: 'USA', flag: '🇺🇸' },
    { name: 'India', flag: '🇮🇳' },
    { name: 'South Africa', flag: '🇿🇦' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section - Modern Bento Style */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="reveal-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                  <Sparkles className="w-4 h-4 text-[#ff6b35]" />
                  <span className="text-sm font-medium text-gray-700">About UZVI Services</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="about-hero-title">
                  Building Tomorrow's
                  <span className="block bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x">
                    Tech Solutions
                  </span>
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-lg">
                  {companyInfo?.description || 'We are professional in SAP, Java, Azure, .Net, Web Development and Database Services. Serving customers worldwide with excellent strategic and technical support.'}
                </p>

                <a 
                  href="/contact"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
                  data-testid="about-hero-cta"
                >
                  Work With Us
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>

              {/* Right - Stats Bento Grid */}
              <div className="reveal-right grid grid-cols-2 gap-4">
                <div className="bento-card bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] text-white p-6 rounded-3xl" data-testid="stat-customers">
                  <Users className="w-8 h-8 mb-4 opacity-80" />
                  <div className="text-4xl font-bold mb-1">{stats.customers}</div>
                  <div className="text-white/80 font-medium">Happy Customers</div>
                </div>
                
                <div className="bento-card bg-gray-900 text-white p-6 rounded-3xl" data-testid="stat-projects">
                  <Award className="w-8 h-8 mb-4 text-[#ff6b35]" />
                  <div className="text-4xl font-bold mb-1">{stats.projects_done}+</div>
                  <div className="text-gray-400 font-medium">Projects Done</div>
                </div>
                
                <div className="col-span-2 bento-card bg-white p-6 rounded-3xl border border-gray-100" data-testid="stat-success">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-5xl font-bold text-gray-900">{stats.results_guaranteed}%</div>
                      <div className="text-gray-500 font-medium">Success Rate</div>
                    </div>
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section - Bento Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-orange-50 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Our Values
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                What Drives Us Forward
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="values-grid">
              {values.map((value, index) => (
                <div 
                  key={value.title}
                  className="reveal-scale bento-card group cursor-pointer"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-7 h-7 text-[#ff6b35]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="reveal-left">
                <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                  Our Mission
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Empowering Businesses Through Technology
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  At UZVI Services, we are committed to delivering exceptional IT solutions that drive business growth and operational excellence. Our team of experienced professionals brings together expertise in SAP, cloud computing, web development, and database management.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {['Expert Technical Team', 'Global Delivery', '24/7 Support', 'Proven Track Record'].map((item, i) => (
                    <div key={item} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="reveal-right relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-orange-500/10">
                  <img
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                    alt="Team collaboration"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-3">
                        {[1,2,3].map((i) => (
                          <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] border-2 border-white"></div>
                        ))}
                      </div>
                      <div className="text-white">
                        <div className="font-semibold">Expert Team</div>
                        <div className="text-white/70 text-sm">Ready to help you grow</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Global Presence - Modern Card Layout */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ff8f5c] rounded-full filter blur-[128px] opacity-10"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white/10 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Global Presence
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Everywhere on the Globe
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                With success stories spanning continents, our presence is felt worldwide.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="locations-grid">
              {locations.map((location, i) => (
                <div 
                  key={location.name}
                  className="reveal-scale group bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10 hover:bg-white/10 hover:border-[#ff6b35]/50 transition-all duration-300 cursor-pointer"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="text-4xl mb-4">{location.flag}</div>
                  <h3 className="text-white font-semibold text-lg">{location.name}</h3>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center reveal">
              <Globe className="w-16 h-16 text-[#ff6b35]/50 mx-auto mb-4 animate-pulse-slow" />
              <p className="text-gray-400">
                Delivering excellence across <span className="text-white font-semibold">4 continents</span>
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 reveal">
              Ready to Transform Your Business?
            </h2>
            <p className="text-white/90 text-lg mb-10 reveal" style={{ animationDelay: '0.1s' }}>
              Let's discuss how we can help you achieve your technology goals.
            </p>
            <a 
              href="/contact"
              className="reveal inline-flex items-center gap-3 bg-white text-[#ff6b35] px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: '0.2s' }}
              data-testid="about-cta-contact"
            >
              Get In Touch
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default AboutPage;
